import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Music, TrendingUp, Clock, Code2, Users, ChevronRight, UserCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { GenreCard } from './GenreCard';
import { SequenceCard } from './SequenceCard';
import { SequencerCard } from './SequencerCard';
import { useHolidayDiscoveryData } from '@/hooks/useHolidayDiscoveryData';
import { useTopSequencers } from '@/hooks/useTopSequencers';

interface HolidayDiscoverySectionProps {
  holiday: string;
  facets: any;
  className?: string;
}

/**
 * Discovery section for holiday-specific pages (Christmas, Halloween, etc.)
 * Displays multiple carousels and discovery features:
 * - Popular Genres within this holiday
 * - Trending sequences for this holiday
 * - Recently added sequences
 * - Software type filters
 * - Top artists
 *
 * This component enhances the holiday landing pages with e-commerce style
 * catalog discovery patterns before users reach the search/filter interface.
 */
export const HolidayDiscoverySection: React.FC<HolidayDiscoverySectionProps> = ({
  holiday,
  facets,
  className,
}) => {
  const { trending, latest, isLoading, error } = useHolidayDiscoveryData(holiday);
  const {
    sequencers,
    isLoading: sequencersLoading,
    error: sequencersError,
  } = useTopSequencers({ holiday, limit: 8 });

  // Extract facet data for genres, software, and artists
  const genres = facets?.genre_canonical?.counts || [];
  const software = facets?.software_type?.counts || [];
  const artists = facets?.artist_name?.counts || [];

  // Get top 12 genres for this holiday
  const topGenres = genres
    .sort((a: any, b: any) => b.count - a.count)
    .slice(0, 12)
    .map((g: any) => ({
      name: g.value,
      slug: g.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
      count: g.count,
    }));

  // Get software types with counts
  const softwareTypes = software
    .filter((s: any) => s.value && s.value !== 'Unknown')
    .sort((a: any, b: any) => b.count - a.count)
    .slice(0, 6);

  // Get top 8 artists
  const topArtists = artists
    .filter((a: any) => a.value && a.value !== 'Unknown Artist')
    .sort((a: any, b: any) => b.count - a.count)
    .slice(0, 8);

  if (error) {
    return (
      <div className={cn('container py-6', className)}>
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            <p>Error loading discovery content. Please try refreshing the page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn('space-y-12', className)}>
      {/* Popular Genres Section */}
      {topGenres.length > 0 && (
        <section className="container py-8 bg-muted/30">
          <div className="mb-8">
            <h2 className="text-3xl font-bold flex items-center gap-2">
              <Music className="h-8 w-8 text-primary" />
              Popular Genres in {holiday}
            </h2>
            <p className="text-muted-foreground mt-2">
              Browse {holiday} sequences by music genre
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {topGenres.map((genre) => (
              <GenreCard
                key={genre.slug}
                name={genre.name}
                slug={genre.slug}
                count={genre.count}
                holiday={holiday}
              />
            ))}
          </div>
        </section>
      )}

      {/* Trending Sequences Carousel */}
      {(isLoading || trending.length > 0) && (
        <section className="container py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold flex items-center gap-2">
                <TrendingUp className="h-8 w-8 text-primary" />
                Trending {holiday} Sequences
              </h2>
              <p className="text-muted-foreground mt-2">Most downloaded this month</p>
            </div>
            <Link to={`/sequences/holiday/${holiday}?sort=download_count:desc`}>
              <Button variant="outline">
                View All <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <Card key={i}>
                  <Skeleton className="aspect-video w-full" />
                  <CardContent className="p-4">
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-4 w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {trending.slice(0, 8).map((sequence) => (
                <SequenceCard key={sequence.id} sequence={sequence} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Recently Added Carousel */}
      {(isLoading || latest.length > 0) && (
        <section className="container py-8 bg-muted/30">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold flex items-center gap-2">
                <Clock className="h-8 w-8 text-primary" />
                Latest {holiday} Uploads
              </h2>
              <p className="text-muted-foreground mt-2">Recently added sequences</p>
            </div>
            <Link to={`/sequences/holiday/${holiday}?sort=created_at:desc`}>
              <Button variant="outline">
                View All <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <Card key={i}>
                  <Skeleton className="aspect-video w-full" />
                  <CardContent className="p-4">
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-4 w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {latest.slice(0, 8).map((sequence) => (
                <SequenceCard key={sequence.id} sequence={sequence} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Browse by Software Section */}
      {softwareTypes.length > 0 && (
        <section className="container py-8">
          <div className="mb-8">
            <h2 className="text-3xl font-bold flex items-center gap-2">
              <Code2 className="h-8 w-8 text-primary" />
              Browse by Software
            </h2>
            <p className="text-muted-foreground mt-2">
              Filter {holiday} sequences by lighting software
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {softwareTypes.map((sw: any) => (
              <Link
                key={sw.value}
                to={`/sequences/search?holiday=${encodeURIComponent(holiday)}&software=${encodeURIComponent(sw.value)}`}
              >
                <Card className="group hover:border-primary hover:shadow-md transition-all cursor-pointer">
                  <CardContent className="p-6 text-center">
                    <div className="mb-2 flex justify-center">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/10 flex items-center justify-center group-hover:from-blue-500/30 group-hover:to-purple-500/20 transition-all">
                        <Code2 className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <h3 className="font-semibold group-hover:text-primary transition-colors">
                      {sw.value}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {sw.count} sequences
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Top Artists Section */}
      {topArtists.length > 0 && (
        <section className="container py-8 bg-muted/30">
          <div className="mb-8">
            <h2 className="text-3xl font-bold flex items-center gap-2">
              <Users className="h-8 w-8 text-primary" />
              Top {holiday} Artists
            </h2>
            <p className="text-muted-foreground mt-2">
              Most popular artists in {holiday} sequences
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {topArtists.map((artist: any) => (
              <Link
                key={artist.value}
                to={`/sequences/search?sequences_v2%5BrefinementList%5D%5Bholiday_type%5D%5B0%5D=${encodeURIComponent(holiday)}&sequences_v2%5BrefinementList%5D%5Bartist_name%5D%5B0%5D=${encodeURIComponent(artist.value)}`}
              >
                <Card className="group hover:border-primary hover:shadow-md transition-all cursor-pointer">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/10 flex items-center justify-center group-hover:from-purple-500/30 group-hover:to-pink-500/20 transition-all shrink-0">
                        <Users className="h-5 w-5 text-purple-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold truncate group-hover:text-primary transition-colors">
                          {artist.value}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {artist.count} sequences
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Top Sequencers Section */}
      {(sequencersLoading || sequencers.length > 0) && !sequencersError && (
        <section className="container py-8">
          <div className="mb-8">
            <h2 className="text-3xl font-bold flex items-center gap-2">
              <UserCircle className="h-8 w-8 text-primary" />
              Top {holiday} Sequencers
            </h2>
            <p className="text-muted-foreground mt-2">
              Most prolific creators of {holiday} sequences
            </p>
          </div>

          {sequencersLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Skeleton className="w-12 h-12 rounded-full" />
                      <div className="flex-1">
                        <Skeleton className="h-4 w-24 mb-2" />
                        <Skeleton className="h-3 w-16" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {sequencers.map((sequencer) => (
                <SequencerCard
                  key={sequencer.id}
                  userId={sequencer.userId}
                  displayName={sequencer.displayName}
                  username={sequencer.username}
                  avatarUrl={sequencer.avatarUrl}
                  sequenceCount={sequencer.sequenceCount}
                  holiday={holiday}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Quick Filters */}
      <section className="container py-8">
        <h2 className="text-3xl font-bold mb-8 text-center">Quick Filters</h2>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to={`/sequences/search?holiday=${encodeURIComponent(holiday)}&free=true`}>
            <Badge
              variant="secondary"
              className="px-6 py-3 text-base cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              Free Only
            </Badge>
          </Link>
          <Link to={`/sequences/search?holiday=${encodeURIComponent(holiday)}&sort=download_count:desc`}>
            <Badge
              variant="secondary"
              className="px-6 py-3 text-base cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              <TrendingUp className="mr-2 h-4 w-4" />
              Most Popular
            </Badge>
          </Link>
          <Link to={`/sequences/search?holiday=${encodeURIComponent(holiday)}&sort=created_at:desc`}>
            <Badge
              variant="secondary"
              className="px-6 py-3 text-base cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              <Clock className="mr-2 h-4 w-4" />
              Latest
            </Badge>
          </Link>
          <Link to={`/sequences/search?holiday=${encodeURIComponent(holiday)}&featured=true`}>
            <Badge
              variant="secondary"
              className="px-6 py-3 text-base cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              Featured
            </Badge>
          </Link>
        </div>
      </section>
    </div>
  );
};
